import { type CollabManager } from '@databases/collab-state-manager/collab-manager';
import { defaultStoreUtils } from '@databases/redux';
import { deferDatabaseEntryProjection } from '@databases/redux/collab/actions';
import { applyDatabaseDiff } from '@databases/utils/redux/databases/actions';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { getExperimentValue } from '@canvas/client-feature-flags';

import {
  getBackendAgentDeletedEntryIds,
  isEntryOrderingChangeDeletionOnly
} from '../backend-agent-entry-deletions.ts';

export const PROGRESSIVE_ENTRY_DELETE_REVEAL_DELAY_MS = 700;

const MAX_REVEAL_TICKS = 4;

type DatabaseTransaction = Parameters<CollabManager['database']['getDiff']>[0];

interface QueuedEntryDelete {
  entryId: string;
}

interface UseProgressiveEntryDeleteRevealOptions {
  collabManager: CollabManager | undefined;
  contentId: string;
  isPaused: boolean;
  layoutRoot: HTMLElement | null;
  onEligibleBackendAgentEntryDelete?: () => void;
  resetKey: string;
}

export type ProgressiveEntryDeleteRevealResult =
  | { kind: 'idle' }
  | { activeEntryId: string; kind: 'revealing' };

function getRenderedBackendAgentEntryIds(
  layoutRoot: HTMLElement,
  candidateEntryIds: readonly string[]
): ReadonlySet<string> {
  const candidateEntryIdSet = new Set(candidateEntryIds);
  const renderedEntryIds = new Set<string>();

  for (const element of layoutRoot.querySelectorAll<HTMLElement>(
    'tr[data-entry-id], [data-testid="database-entry-card-container"][data-entry-id], [data-timeline-fixed-row-entry-id][data-entry-id]'
  )) {
    const entryId = element.getAttribute('data-entry-id');
    if (!entryId || !candidateEntryIdSet.has(entryId)) {
      continue;
    }

    const rect = element.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      renderedEntryIds.add(entryId);
    }
  }

  return renderedEntryIds;
}

export function useProgressiveEntryDeleteReveal({
  collabManager,
  contentId,
  isPaused,
  layoutRoot,
  onEligibleBackendAgentEntryDelete,
  resetKey
}: UseProgressiveEntryDeleteRevealOptions): ProgressiveEntryDeleteRevealResult {
  const isDatabaseBEStreamingEnabled = getExperimentValue(
    'databaseBEStreaming',
    'enabled',
    { fireExperimentExposure: false }
  );
  const dispatch = defaultStoreUtils.useDispatch();
  const previousEntryIds = useRef<readonly string[]>([]);
  const revealBatchSize = useRef(1);
  const activeBatchSize = useRef(1);
  const queuedEntryDeletesRef = useRef<readonly QueuedEntryDelete[]>([]);
  const [queuedEntryDeletes, setQueuedEntryDeletes] = useState<
    readonly QueuedEntryDelete[]
  >([]);
  const activeEntryDelete =
    queuedEntryDeletes[Math.min(activeBatchSize.current, queuedEntryDeletes.length) - 1];

  const replaceQueuedEntryDeletes = useCallback(
    (nextQueuedEntryDeletes: readonly QueuedEntryDelete[]) => {
      queuedEntryDeletesRef.current = nextQueuedEntryDeletes;
      if (nextQueuedEntryDeletes.length === 0) {
        revealBatchSize.current = 1;
        activeBatchSize.current = 1;
      }
      setQueuedEntryDeletes(nextQueuedEntryDeletes);
    },
    []
  );

  const projectEntryDeletions = useCallback(
    (entryIds: readonly string[]) => {
      if (!collabManager || entryIds.length === 0) {
        return;
      }

      dispatch(
        applyDatabaseDiff({
          contentId,
          diff: {
            fieldsOrderingChanged: false,
            entriesOrderingChanged: false,
            changedFieldIds: [],
            changedEntryIds: [...entryIds],
            threadsChanged: false,
            viewsChanged: false,
            defaultEntryChanged: false,
            othersChanged: false
          },
          collabDatabase: collabManager.database,
          isReadOnly: collabManager.isReadOnly
        })
      );
    },
    [collabManager, contentId, dispatch]
  );

  useLayoutEffect(() => {
    queuedEntryDeletesRef.current = [];
    revealBatchSize.current = 1;
    activeBatchSize.current = 1;
    setQueuedEntryDeletes([]);

    if (!collabManager) {
      previousEntryIds.current = [];
      return;
    }

    previousEntryIds.current = collabManager.database.entries;
    const onAfterTransaction = (transaction: DatabaseTransaction) => {
      const diff = collabManager.database.getDiff(transaction);
      const currentEntryIds = collabManager.database.entries;
      const previousTransactionEntryIds = previousEntryIds.current;
      previousEntryIds.current = currentEntryIds;

      if (!diff.entriesOrderingChanged) {
        return;
      }

      const deletedEntryIds = getBackendAgentDeletedEntryIds({
        currentEntryIds,
        previousEntryIds: previousTransactionEntryIds,
        transaction
      });

      if (
        deletedEntryIds.length === 0 ||
        !isEntryOrderingChangeDeletionOnly({
          currentEntryIds,
          deletedEntryIds,
          previousEntryIds: previousTransactionEntryIds
        })
      ) {
        if (queuedEntryDeletesRef.current.length > 0) {
          projectEntryDeletions(
            queuedEntryDeletesRef.current.map(({ entryId }) => entryId)
          );
          replaceQueuedEntryDeletes([]);
        }
        return;
      }

      if (!layoutRoot) {
        return;
      }

      const renderedEntryIds = getRenderedBackendAgentEntryIds(
        layoutRoot,
        deletedEntryIds
      );
      const visibleEntryDeletes = deletedEntryIds.filter((entryId) =>
        renderedEntryIds.has(entryId)
      );
      if (visibleEntryDeletes.length === 0) {
        return;
      }

      onEligibleBackendAgentEntryDelete?.();
      if (!isDatabaseBEStreamingEnabled) {
        return;
      }

      deferDatabaseEntryProjection(transaction, visibleEntryDeletes, {
        deferEntriesOrdering: true
      });
      const nextQueuedEntryDeletes = [
        ...queuedEntryDeletesRef.current,
        ...visibleEntryDeletes.map((entryId) => ({ entryId }))
      ];
      revealBatchSize.current = Math.max(
        revealBatchSize.current,
        Math.ceil((nextQueuedEntryDeletes.length - 1) / MAX_REVEAL_TICKS)
      );
      if (queuedEntryDeletesRef.current.length === 0) {
        activeBatchSize.current = 1;
      }
      replaceQueuedEntryDeletes(nextQueuedEntryDeletes);
    };

    collabManager.database.doc.on('afterTransaction', onAfterTransaction);
    return () => {
      collabManager.database.doc.off('afterTransaction', onAfterTransaction);
      projectEntryDeletions(queuedEntryDeletesRef.current.map(({ entryId }) => entryId));
      queuedEntryDeletesRef.current = [];
    };
  }, [
    collabManager,
    isDatabaseBEStreamingEnabled,
    layoutRoot,
    onEligibleBackendAgentEntryDelete,
    projectEntryDeletions,
    replaceQueuedEntryDeletes,
    resetKey
  ]);

  useEffect(() => {
    if (!activeEntryDelete || isPaused) {
      return;
    }

    const timeout = setTimeout(() => {
      const batchSize = activeBatchSize.current;
      const currentQueuedEntryDeletes = queuedEntryDeletesRef.current;
      projectEntryDeletions(
        currentQueuedEntryDeletes.slice(0, batchSize).map(({ entryId }) => entryId)
      );
      const nextQueuedEntryDeletes = currentQueuedEntryDeletes.slice(batchSize);
      activeBatchSize.current = Math.min(
        revealBatchSize.current,
        nextQueuedEntryDeletes.length
      );
      replaceQueuedEntryDeletes(nextQueuedEntryDeletes);
    }, PROGRESSIVE_ENTRY_DELETE_REVEAL_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [activeEntryDelete, isPaused, projectEntryDeletions, replaceQueuedEntryDeletes]);

  return activeEntryDelete
    ? { activeEntryId: activeEntryDelete.entryId, kind: 'revealing' }
    : { kind: 'idle' };
}
